/**
 * Step 2 review table — renders one row per parsed CSV row with per-cell
 * error highlighting and a status pill at the end.
 *
 * Extracted from `ImportPetsModal.tsx` to keep the orchestrator focused
 * on flow + state. The table itself is pure: it receives already-verified
 * rows and renders them.
 */

import React from 'react';
import styles from '../Integrations.module.scss';
import { ErrorTooltipCell, StatusPill } from './ErrorTooltip';
import {
  displayRowNumber,
  errorFieldsForRow,
  errorsByFieldForRow,
  type ParsedRow,
  shortStatusFor,
} from './validateRow';
import { valueOrDash } from './uiHelpers';

interface ReviewTableProps {
  rows: ParsedRow[];
  /** Shown when `rows` is empty — caller decides the message. */
  emptyMessage: string;
}

function rowStatusLabel(rawErrors: ParsedRow['rawErrors']): string {
  const numErrors = rawErrors.length;
  if (numErrors === 0) {
    return 'Valid';
  }
  if (numErrors > 1) {
    return `${numErrors} issues`;
  }
  // The `if numErrors > 1` branch above ensures `rawErrors[0]` exists when
  // we get here, but TS can't narrow through that. Belt-and-suspenders.
  const first = rawErrors[0];
  return first ? shortStatusFor(first) : 'Issue';
}

export default function ReviewTable({ rows, emptyMessage }: ReviewTableProps) {
  return (
    <div className={styles.reviewCard}>
      <div className={styles.reviewScroll}>
        <table className={styles.reviewTable}>
          <thead>
            <tr>
              <th className={styles.reviewColRow}>Row</th>
              <th>Microchip number</th>
              <th>Pet</th>
              <th>Owner</th>
              <th>Email</th>
              <th>Phone</th>
              <th className={styles.reviewColStatus}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const errFields = errorFieldsForRow(r.rawErrors);
              const fieldErrors = errorsByFieldForRow(r.rawErrors);
              const ownerName = [r.ownerFirstName, r.ownerLastName].filter(Boolean).join(' ');
              const petLabel = [r.name, r.breed].filter(Boolean).join(' · ');
              const isError = r.rawErrors.length > 0;
              const statusLabel = rowStatusLabel(r.rawErrors);
              const chipErrors = fieldErrors.get('microchip_id') ?? [];
              const petErrors = [...(fieldErrors.get('pet_name') ?? []), ...(fieldErrors.get('breed') ?? [])];
              const ownerErrors = [
                ...(fieldErrors.get('owner_first_name') ?? []),
                ...(fieldErrors.get('owner_last_name') ?? []),
              ];
              const emailErrors = fieldErrors.get('owner_email') ?? [];
              const phoneErrors = fieldErrors.get('owner_phone') ?? [];
              return (
                <tr key={r.rowIndex} className={isError ? styles.reviewRowError : ''}>
                  <td className={styles.reviewColRow}>{displayRowNumber(r.rowIndex)}</td>
                  <ErrorTooltipCell
                    errors={chipErrors}
                    rowIndex={r.rowIndex}
                    field="microchip_id"
                    className={`${styles.mono} ${errFields.has('microchip_id') ? styles.errCell : ''}`}
                  >
                    {valueOrDash(r.chipSerialNumber)}
                  </ErrorTooltipCell>
                  <ErrorTooltipCell
                    errors={petErrors}
                    rowIndex={r.rowIndex}
                    field="pet"
                    className={errFields.has('pet_name') || errFields.has('breed') ? styles.errCell : ''}
                  >
                    <span className={styles.ellip}>{valueOrDash(petLabel)}</span>
                  </ErrorTooltipCell>
                  <ErrorTooltipCell
                    errors={ownerErrors}
                    rowIndex={r.rowIndex}
                    field="owner"
                    className={
                      errFields.has('owner_first_name') || errFields.has('owner_last_name') ? styles.errCell : ''
                    }
                  >
                    <span className={styles.ellip}>{valueOrDash(ownerName)}</span>
                  </ErrorTooltipCell>
                  <ErrorTooltipCell
                    errors={emailErrors}
                    rowIndex={r.rowIndex}
                    field="owner_email"
                    className={errFields.has('owner_email') ? styles.errCell : ''}
                  >
                    <span className={styles.ellip} title={r.ownerEmail || undefined}>
                      {valueOrDash(r.ownerEmail)}
                    </span>
                  </ErrorTooltipCell>
                  <ErrorTooltipCell
                    errors={phoneErrors}
                    rowIndex={r.rowIndex}
                    field="owner_phone"
                    className={errFields.has('owner_phone') ? styles.errCell : ''}
                  >
                    <span className={styles.ellip}>{valueOrDash(r.ownerPhone)}</span>
                  </ErrorTooltipCell>
                  <td className={styles.reviewColStatus}>
                    <StatusPill isError={isError} label={statusLabel} errors={r.rawErrors} rowIndex={r.rowIndex} />
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className={styles.reviewEmpty}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
